import type { PosCatalogItemTypeFilter } from '../domain/posTypes';

const ITEM_TYPE_PHRASE: Record<Exclude<PosCatalogItemTypeFilter, 'all'>, string> = {
  physical: 'solo productos',
  service: 'solo servicios',
  package: 'solo paquetes',
};

const ITEM_TYPE_CHIP_LABEL: Record<Exclude<PosCatalogItemTypeFilter, 'all'>, string> = {
  physical: 'Productos',
  service: 'Servicios',
  package: 'Paquetes',
};

export type PosCatalogActiveFilterChipKey =
  | 'category'
  | 'itemType'
  | 'stock'
  | 'brand'
  | 'supplier';

export type PosCatalogActiveFilterChip = {
  key: PosCatalogActiveFilterChipKey;
  label: string;
};

export type PosActiveCatalogFiltersSummaryParams = {
  selectedCategory: string | 'all' | null;
  categoryLabel: string;
  itemTypeFilter: PosCatalogItemTypeFilter;
  posPhysicalStockOnly: boolean;
  posBrandFilter: string | null;
  posBrandName?: string | null;
  posSupplierFilter: string | null;
  posSupplierName?: string | null;
};

function joinFilterPhrases(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} y ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`;
}

function describeActiveFilterPhrases(params: PosActiveCatalogFiltersSummaryParams): string[] {
  const parts: string[] = [];
  if (params.selectedCategory != null && params.selectedCategory !== 'all') {
    parts.push(`categoría «${params.categoryLabel}»`);
  }
  if (params.itemTypeFilter !== 'all') {
    parts.push(ITEM_TYPE_PHRASE[params.itemTypeFilter] ?? params.itemTypeFilter);
  }
  if (params.posPhysicalStockOnly) {
    parts.push('solo con stock');
  }
  if (params.posBrandFilter) {
    const name = params.posBrandName?.trim();
    parts.push(name ? `marca «${name}»` : 'marca');
  }
  if (params.posSupplierFilter) {
    const name = params.posSupplierName?.trim();
    parts.push(name ? `proveedor «${name}»` : 'proveedor');
  }
  return parts;
}

/** Fragmentos con « · » (debug / resumen compacto). */
export function buildPosCatalogActiveFiltersSummary(
  params: PosActiveCatalogFiltersSummaryParams,
): string | null {
  const parts = describeActiveFilterPhrases(params);
  if (parts.length === 0) return null;
  return parts.join(' · ');
}

/** Chips removibles bajo el buscador (sin búsqueda por texto). */
export function listPosCatalogActiveFilterChips(
  params: PosActiveCatalogFiltersSummaryParams,
): PosCatalogActiveFilterChip[] {
  const chips: PosCatalogActiveFilterChip[] = [];

  if (params.selectedCategory != null && params.selectedCategory !== 'all') {
    chips.push({ key: 'category', label: params.categoryLabel });
  }
  if (params.itemTypeFilter !== 'all') {
    chips.push({
      key: 'itemType',
      label: ITEM_TYPE_CHIP_LABEL[params.itemTypeFilter] ?? params.itemTypeFilter,
    });
  }
  if (params.posPhysicalStockOnly) {
    chips.push({ key: 'stock', label: 'Solo con stock' });
  }
  if (params.posBrandFilter) {
    const name = params.posBrandName?.trim();
    chips.push({ key: 'brand', label: name || 'Marca' });
  }
  if (params.posSupplierFilter) {
    const name = params.posSupplierName?.trim();
    chips.push({ key: 'supplier', label: name || 'Proveedor' });
  }

  return chips;
}

export function hasPosCatalogPanelFilters(params: PosActiveCatalogFiltersSummaryParams): boolean {
  return listPosCatalogActiveFilterChips(params).length > 0;
}
